/**
 * The one hook that makes a mounted board live (spec M0-F5 §6, §8).
 *
 * It joins the room, feeds every envelope through the reducer into the store, keeps presence
 * alive, and handles the four things that can go wrong: the socket drops (reconnect with a
 * watermark), the delta is too big (`syncRequired` → reload), the queue overflows
 * (`resync-required` → reload), and access is revoked mid-session (`room:revoked` → the
 * no-access panel).
 *
 * The **watermark is always the server's `now`**, never a locally computed time: a client
 * with a wrong clock is a client that silently misses changes (§5.6, E5).
 *
 * When the socket cannot be established at all — a proxy that blocks websockets — the board
 * is not dead: `realtime.sync` is polled on window focus and every 30 s, so it is stale but
 * correct rather than wrong (§5.4, §5.6, S5).
 */
import { useEffect, useRef } from 'react';
import type { EventEnvelope, PresenceRoster } from '@elizmonday/shared';
import { SeenEvents, applyEvent, type PatchContext, type StorePatch } from './patches';
import {
  enterPresence,
  joinBoard,
  leaveBoard,
  onEvent,
  onServerNotice,
  onRealtimeStatus,
  type RealtimeStatus,
} from './socket';

/** Poll interval for the degraded path, when there is no websocket at all (§5.6). */
export const DEGRADED_POLL_MS = 30_000;

export interface BoardRealtimeTarget {
  boardId: string;
  viewId: string | null;
  /** Apply a reduced patch; the store decides what each kind means. */
  applyRemotePatch(patch: StorePatch): void;
  setPresence(roster: PresenceRoster | null): void;
  setAccessRevoked(): void;
  /** Throw the board state away and load it again (truncated / resync-required). */
  reload(): void;
  /** Is this mutation id one of ours, still in flight (§5.3)? */
  isOwnPending(mutationId: string): boolean;
  /** The `updated_at` the store holds for an entity key, for the stale-patch rule. */
  updatedAtOf(key: string): string | null | undefined;
  onStatus?(status: RealtimeStatus): void;
  /** Pull a delta over HTTP; the degraded path and the `syncRequired` path both use it. */
  sync(since: string | null): Promise<{ now: string; truncated: boolean } | null>;
}

export function useBoardRealtime(target: BoardRealtimeTarget | null): void {
  // The target changes on every render (it closes over the store); keep the effect stable by
  // reading it through a ref rather than by listing it as a dependency.
  const ref = useRef(target);
  ref.current = target;
  const boardId = target?.boardId ?? null;
  const viewId = target?.viewId ?? null;

  useEffect(() => {
    if (!boardId) return;
    let cancelled = false;
    let watermark: string | null = null;
    let leavePresence: (() => void) | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    const seen = new SeenEvents();

    const current = () => ref.current;

    const context = (): PatchContext => ({
      boardId,
      updatedAtOf: (key) => current()?.updatedAtOf(key),
      isOwnPending: (mutationId) => current()?.isOwnPending(mutationId) ?? false,
    });

    const pull = async (): Promise<void> => {
      const owner = current();
      if (!owner) return;
      const result = await owner.sync(watermark).catch(() => null);
      if (!result || cancelled) return;
      watermark = result.now;
      // `truncated` means the delta was not safe to apply piecemeal: reload (S3).
      if (result.truncated) owner.reload();
    };

    const join = async (): Promise<void> => {
      const owner = current();
      if (!owner) return;
      const result = await joinBoard(boardId, watermark);
      if (cancelled) return;

      if (result === 'forbidden') {
        owner.setAccessRevoked();
        return;
      }
      if (result === null) {
        // No ack: the api is down or the websocket is blocked. Stale but correct (S5).
        void pull();
        return;
      }

      watermark = result.serverTime ?? watermark;
      const sync = result.sync as { syncRequired?: boolean; truncated?: boolean } | undefined;
      if (sync?.syncRequired || sync?.truncated) owner.reload();
      else if (sync) owner.reload();

      leavePresence?.();
      leavePresence = enterPresence(boardId, viewId);
    };

    const offEvent = onEvent((event: EventEnvelope) => {
      const owner = current();
      if (!owner) return;
      // Another board's room on the same socket: not ours to apply.
      if (event.boardId && event.boardId !== boardId) return;
      owner.applyRemotePatch(applyEvent(event, context(), seen));
    });

    const offNotice = onServerNotice({
      presence: (roster) => {
        if (roster.boardId === boardId) current()?.setPresence(roster);
      },
      roomRevoked: (payload) => {
        if (payload.boardId === boardId) current()?.setAccessRevoked();
      },
      resyncRequired: () => {
        // The server cleared our queue rather than grow without bound (§5.8, X4).
        current()?.reload();
      },
      connected: () => {
        // Every room must be re-joined explicitly after a reconnect, with the watermark.
        void join();
      },
    });

    const offStatus = onRealtimeStatus((status) => {
      current()?.onStatus?.(status);
      if (status === 'degraded' && pollTimer === null) {
        void pull();
        pollTimer = setInterval(() => void pull(), DEGRADED_POLL_MS);
      }
      if (status === 'live' && pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    });

    const onFocus = () => {
      // Cheap, and the only thing that saves a tab that was asleep through a disconnection.
      void pull();
    };
    window.addEventListener('focus', onFocus);

    void join();

    return () => {
      cancelled = true;
      offEvent();
      offNotice();
      offStatus();
      window.removeEventListener('focus', onFocus);
      if (pollTimer !== null) clearInterval(pollTimer);
      leavePresence?.();
      leaveBoard(boardId);
      current()?.setPresence(null);
    };
  }, [boardId, viewId]);
}
